/**
 * Member CO Points wallet (autoFillPoints) + genesis / admin CO bucket (autoFillCoPoints).
 */
import { sumPointsCreditedFromLedgerRows, cappedCoPointsEarnedAtLevel } from "../services/autoFill/autoFillLevelForfeiturePure";
import { filterWithdrawalsForProgram } from "../config/autoFillWithdrawalSequencePolicy";
import { isRootAutoFillProgram } from "../services/autoFill/autoFillProgramSpawn";
import { AUTO_FILL_MAX_LEVEL, getLevelAmount } from "../config/autoFillProgramConfig";

export function getAutoFillCoPointsWallet(userProfile) {
  const points = userProfile?.autoFillPoints || {};
  const co = userProfile?.autoFillCoPoints || {};

  const available = (Number(points.available) || 0) + (Number(co.available) || 0);
  const earned = (Number(points.earned) || 0) + (Number(co.earned) || 0);
  const withdrawn = (Number(points.withdrawn) || 0) + (Number(co.withdrawn) || 0);
  const locked = (Number(points.locked) || 0) + (Number(co.locked) || 0);

  return {
    available,
    earned,
    withdrawn,
    locked,
    autoFillPoints: {
      available: Number(points.available) || 0,
      earned: Number(points.earned) || 0,
      withdrawn: Number(points.withdrawn) || 0,
      locked: Number(points.locked) || 0,
    },
    autoFillCoPoints: {
      available: Number(co.available) || 0,
      earned: Number(co.earned) || 0,
      withdrawn: Number(co.withdrawn) || 0,
      locked: Number(co.locked) || 0,
    },
  };
}

/** CO Points earned / outflows for one AFP program (ledger + withdrawals). */
export function computeProgramCoPointsWallet(
  program,
  ledgerRows,
  userId,
  withdrawalRows,
  {
    includeGenesisAdminCo = false,
    userCoBucket = null,
    memberPointsWallet = null,
    /** When set, level L CO counts only if matrix has ≥1 filled slot at L on this AFP. */
    filledMatrixCountByLevel = null,
  } = {}
) {
  const programId = program?.programId;
  if (!programId || !userId) {
    return { available: 0, earned: 0, withdrawn: 0, locked: 0 };
  }

  const programLedger = (ledgerRows || []).filter(
    (r) => r.userId === userId && r.programId === programId
  );
  let earned = 0;
  const useMatrixGate = filledMatrixCountByLevel != null;
  for (let L = 1; L <= AUTO_FILL_MAX_LEVEL; L += 1) {
    const filledAtL = useMatrixGate
      ? Number(filledMatrixCountByLevel[L] ?? filledMatrixCountByLevel[String(L)] ?? 0)
      : null;
    if (useMatrixGate && filledAtL === 0) {
      continue;
    }
    const capped = useMatrixGate
      ? cappedCoPointsEarnedAtLevel(
          programId,
          userId,
          L,
          programLedger,
          filledAtL
        )
      : Math.min(
          getLevelAmount(L),
          sumPointsCreditedFromLedgerRows(
            programId,
            userId,
            L,
            programLedger
          )
        );
    earned += capped;
  }

  const programWithdrawals = filterWithdrawalsForProgram(
    withdrawalRows || [],
    program
  );
  let withdrawn = 0;
  let locked = 0;
  programWithdrawals.forEach((w) => {
    const amt = Number(w.amount) || 0;
    if (w.status === "COMPLETED") withdrawn += amt;
    else if (w.status === "PENDING") locked += amt;
  });

  let available = Math.max(0, earned - withdrawn - locked);

  if (memberPointsWallet && !program?.parentProgramId) {
    const uEarned = Number(memberPointsWallet.earned) || 0;
    const uAvail = Number(memberPointsWallet.available) || 0;
    const uWithdrawn = Number(memberPointsWallet.withdrawn) || 0;
    const uLocked = Number(memberPointsWallet.locked) || 0;
    earned = uEarned;
    withdrawn = Math.max(withdrawn, uWithdrawn);
    locked = Math.max(locked, uLocked);
    available = uAvail;
  }

  if (includeGenesisAdminCo && userCoBucket) {
    const coAvail = Number(userCoBucket.available) || 0;
    available += coAvail;
    earned += Number(userCoBucket.earned) || 0;
    withdrawn += Number(userCoBucket.withdrawn) || 0;
    locked += Number(userCoBucket.locked) || 0;
  }

  return { available, earned, withdrawn, locked, programId };
}

/** Split a withdrawal amount across points wallet then admin CO bucket. */
export function splitAutoFillWithdrawalAmount(userData, amount) {
  const amt = Number(amount);
  const points = userData?.autoFillPoints || {};
  const fromPoints = Math.min(amt, Number(points.available) || 0);
  const fromCo = amt - fromPoints;
  return { fromPoints, fromCo, total: amt };
}

/**
 * Spendable CO breakdown for withdrawal UI (matches Wallet & Points totals).
 */
export function getWithdrawalCoPointsBreakdown(userProfile, program, programLedgerWallet) {
  const global = getAutoFillCoPointsWallet(userProfile);
  const isGenesisRoot =
    program &&
    isRootAutoFillProgram(program) &&
    Number(program.enrollmentSeq) === 1;

  const programLedgerView = programLedgerWallet || {
    available: 0,
    earned: 0,
    withdrawn: 0,
    locked: 0,
  };

  /** Spawn / member AFP: totals must match selected program, not combined IW parent wallet. */
  if (program?.programId && !isGenesisRoot) {
    return {
      totalAvailable: programLedgerView.available,
      totalEarned: programLedgerView.earned,
      totalWithdrawn: programLedgerView.withdrawn,
      totalLocked: programLedgerView.locked,
      matrixCoPoints: {
        available: programLedgerView.available,
        earned: programLedgerView.earned,
        withdrawn: programLedgerView.withdrawn,
        locked: programLedgerView.locked,
      },
      genesisAdminCo: {
        available: 0,
        earned: 0,
        withdrawn: 0,
        locked: 0,
      },
      isGenesisRoot: false,
      programLedgerView,
      programId: program.programId,
    };
  }

  const matrixCoPoints = {
    available: global.autoFillPoints.available,
    earned: global.autoFillPoints.earned,
    withdrawn: global.autoFillPoints.withdrawn,
    locked: global.autoFillPoints.locked,
  };

  const genesisAdminCo = isGenesisRoot
    ? {
        available: global.autoFillCoPoints.available,
        earned: global.autoFillCoPoints.earned,
        withdrawn: global.autoFillCoPoints.withdrawn,
        locked: global.autoFillCoPoints.locked,
      }
    : {
        available: 0,
        earned: 0,
        withdrawn: 0,
        locked: 0,
      };

  return {
    totalAvailable: global.available,
    totalEarned: global.earned,
    totalWithdrawn: global.withdrawn,
    totalLocked: global.locked,
    matrixCoPoints,
    genesisAdminCo,
    isGenesisRoot,
    programLedgerView,
    programId: program?.programId || null,
  };
}
